import React, { useState, useCallback } from 'react';
import type { Message, ProductAction, ProductCard } from '../../types';
import { MESSAGE_TYPES } from '../../constants';
import { renderMarkdown, hasMarkdown } from '../../utils/markdown';
import { useChatConfig } from '../../context/ChatContext';
import { isHiddenAction } from '../../utils/actionFilters';
import MessageActions from './MessageActions';
import AttachmentPreview from '../InputArea/AttachmentPreview';
import ToolCallDisplay from '../rich/ToolCallDisplay';
import SourceCitation from '../rich/SourceCitation';
import ThinkingDisplay from '../rich/ThinkingDisplay';
import CardMessage from '../rich/CardMessage';
import ButtonsMessage from '../rich/ButtonsMessage';
import FormMessage from '../rich/FormMessage';
import ProductCardsMessage from '../rich/ProductCardsMessage';
import OrderCardsMessage from '../rich/OrderCardsMessage';
import type { WidgetStrings } from '../../utils/i18n';
import { DEFAULT_WIDGET_STRINGS } from '../../utils/i18n';

interface OrderCardAction {
  label: string;
  value?: string;
  href?: string;
  target?: '_self' | '_blank';
}

interface MessageBubbleProps {
  message: Message;
  onButtonClick?: (value: string) => void;
  onProductAction?: (payload: { action: ProductAction; product: ProductCard }) => void;
  onOrderAction?: (payload: {
    action: OrderCardAction;
    orderId?: string | null;
    orderName?: string | null;
  }) => void;
  onFormSubmit?: (formId: string | undefined, data: Record<string, string>) => void;
  strings?: WidgetStrings;
  direction?: 'ltr' | 'rtl';
}

const MessageBubble = React.memo(function MessageBubble({
  message,
  onButtonClick,
  onProductAction,
  onOrderAction,
  onFormSubmit,
  strings = DEFAULT_WIDGET_STRINGS,
  direction = 'ltr',
}: MessageBubbleProps) {
  const {
    enableToolVisualization,
    enableSourceCitations,
    enableThinking,
    showTimestamps,
    trackAnalytics,
  } = useChatConfig();
  const isUser = message.sender === 'user';
  const [reaction, setReaction] = useState<'up' | 'down' | null>(message.reaction || null);
  const [showActions, setShowActions] = useState(false);

  const filteredSuggestions = (message.suggestions || []).filter(
    suggestion => !isHiddenAction(suggestion)
  );
  const filteredButtons =
    message.type === MESSAGE_TYPES.BUTTONS && message.richContent?.buttons
      ? message.richContent.buttons.filter(
          button => !isHiddenAction(button.label) && !isHiddenAction(button.value)
        )
      : message.richContent?.buttons;

  const handleReaction = useCallback(
    (type: 'up' | 'down') => {
      setReaction(type);
      trackAnalytics('message_reaction', { messageId: message.id, reaction: type });
    },
    [message.id, trackAnalytics]
  );

  const handleCopy = useCallback(() => {
    const text = message.text || '';
    if (!text) return;
    const trackFailure = (reason: string) => {
      trackAnalytics('message_copy_failed', { messageId: message.id, reason });
    };
    if (typeof navigator !== 'undefined' && navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(
        () => trackAnalytics('message_copied', { messageId: message.id }),
        () => trackFailure('clipboard_error')
      );
      return;
    }
    if (typeof document === 'undefined') {
      trackFailure('clipboard_unavailable');
      return;
    }
    try {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'absolute';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.select();
      const ok = typeof document.execCommand === 'function' && document.execCommand('copy');
      document.body.removeChild(textarea);
      if (ok) {
        trackAnalytics('message_copied', { messageId: message.id, fallback: true });
      } else {
        trackFailure('clipboard_unavailable');
      }
    } catch {
      trackFailure('clipboard_error');
    }
  }, [message.text, message.id, trackAnalytics]);

  const alignRight = direction === 'rtl' ? !isUser : isUser;
  const ariaLabel = isUser ? strings.yourMessageAria : strings.assistantMessageAria;
  const bubbleTailClass = alignRight ? 'bubble-right' : 'bubble-left';

  const Bubble = (
    <div
      className={`bubble rw-bubble ${bubbleTailClass} ${isUser ? 'rw-bubble-user' : 'rw-bubble-agent'} max-w-[80%] rounded-2xl px-4 py-2 border ${
        isUser ? 'bg-gray-100 text-gray-900 border-gray-200' : 'bg-white border-gray-200'
      } relative group focus:outline-2 focus:outline-blue-500`}
      tabIndex={0} // eslint-disable-line jsx-a11y/no-noninteractive-tabindex
      onMouseEnter={() => setShowActions(true)}
      onMouseLeave={e => {
        if (typeof document !== 'undefined' && e.currentTarget.contains(document.activeElement))
          return;
        setShowActions(false);
      }}
      onFocus={() => setShowActions(true)}
      onBlur={e => {
        if (e.currentTarget.contains(e.relatedTarget as Node)) return;
        setShowActions(false);
      }}
      aria-label={ariaLabel}
    >
      {!isUser && showActions && (
        <MessageActions
          reaction={reaction}
          onCopy={handleCopy}
          onReaction={handleReaction}
          strings={strings}
        />
      )}

      {message.text &&
        (hasMarkdown(message.text) ? (
          <div
            className="text-sm markdown-content"
            style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}
            dangerouslySetInnerHTML={{ __html: renderMarkdown(message.text) }}
          />
        ) : (
          <p className="text-sm" style={{ wordBreak: 'break-word', overflowWrap: 'anywhere' }}>
            {message.text}
          </p>
        ))}

      {message.attachments?.map(att => (
        <AttachmentPreview key={att.id} attachment={att} />
      ))}

      {message.toolCalls && message.toolCalls.length > 0 && enableToolVisualization && (
        <div className="mt-3 space-y-2">
          {message.toolCalls.map(tool => (
            <ToolCallDisplay key={tool.id} toolCall={tool} />
          ))}
        </div>
      )}

      {message.sources && message.sources.length > 0 && enableSourceCitations && (
        <div className="mt-3 space-y-1">
          <div className="text-xs font-semibold text-gray-600 mb-1">{strings.sourcesHeading}</div>
          {message.sources.map((source, idx) => (
            <SourceCitation key={source.id || idx} source={source} index={idx + 1} />
          ))}
        </div>
      )}

      {message.thinking && enableThinking && (
        <ThinkingDisplay thinking={message.thinking} strings={strings} />
      )}

      {showTimestamps && message.timestamp && (
        <div className={`mt-1 text-[10px] text-gray-400 ${alignRight ? 'text-right' : ''}`}>
          {message.timestamp}
        </div>
      )}

      {message.type === MESSAGE_TYPES.CARD && message.richContent && (
        <CardMessage content={message.richContent} />
      )}
      {message.type === MESSAGE_TYPES.BUTTONS && message.richContent && (
        <ButtonsMessage
          content={{ ...message.richContent, buttons: filteredButtons }}
          onButtonClick={onButtonClick}
          trackAnalytics={trackAnalytics}
        />
      )}
      {message.type === MESSAGE_TYPES.FORM && message.richContent && (
        <FormMessage
          content={{
            ...message.richContent,
            onSubmit: data => {
              if (onFormSubmit) {
                onFormSubmit(message.richContent?.formId, data);
                return;
              }
              message.richContent?.onSubmit?.(data);
            },
          }}
          strings={strings}
        />
      )}
      {message.type === MESSAGE_TYPES.PRODUCT_CARDS && message.richContent && (
        <ProductCardsMessage content={message.richContent} onAction={onProductAction} />
      )}
      {message.type === MESSAGE_TYPES.ORDER_CARDS && message.richContent && (
        <OrderCardsMessage
          content={message.richContent}
          onButtonClick={onButtonClick}
          onAction={onOrderAction}
          strings={strings}
        />
      )}

      {!isUser && filteredSuggestions.length > 0 && (
        <div className="mt-2">
          <ButtonsMessage
            content={{
              buttons: filteredSuggestions.map(suggestion => ({
                label: suggestion,
                value: suggestion,
              })),
            }}
            onButtonClick={onButtonClick}
            trackAnalytics={trackAnalytics}
          />
        </div>
      )}
    </div>
  );

  return (
    <div
      className={`flex items-end gap-2 w-full ${alignRight ? 'justify-end' : ''} mb-3 animate-fadeIn`}
      role="article"
    >
      {Bubble}
    </div>
  );
});

export default MessageBubble;
